// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';

export interface ProBonoCase {
  id: string;
  case_title: string;
  client_name: string;
  estimated_value: number;
  actual_value: number;
  category_name: string;
  category_color: string;
  status: string;
  start_date: string;
  end_date?: string;
  social_impact_description?: string;
  created_at: string;
}

export interface ProBonoCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
}

export interface ProBonoImpactMetric {
  id: string;
  probono_case_id: string;
  metric_type: string;
  metric_value: number;
  metric_unit: string;
  description?: string;
  recorded_date: string;
}

export interface ProBonoReportData {
  totalCases: number;
  totalValue: number;
  totalHours: number;
  totalPeopleHelped: number;
  categories: {
    [key: string]: {
      count: number;
      value: number;
      color: string;
    };
  };
  monthlyData: {
    month: string;
    cases: number;
    value: number;
    hours: number;
    people: number;
  }[];
  recentCases: ProBonoCase[];
}

/**
 * Serviço para gerenciar relatórios de casos Pro Bono
 */
export class ProBonoReportService {
  /**
   * Busca todos os casos Pro Bono
   */
  static async getProBonoCases(): Promise<ProBonoCase[]> {
    try {
      const { data, error } = await supabase
        .from('probono_summary')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar casos Pro Bono:', error);
      throw error;
    }
  }

  /**
   * Busca categorias Pro Bono
   */
  static async getProBonoCategories(): Promise<ProBonoCategory[]> {
    try {
      const { data, error } = await supabase
        .from('probono_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar categorias Pro Bono:', error);
      throw error;
    }
  }

  /**
   * Gera relatório completo de Pro Bono
   */
  static async generateProBonoReport(startDate?: string, endDate?: string): Promise<ProBonoReportData> {
    try {
      let query = supabase
        .from('probono_summary')
        .select('*');

      if (startDate) {
        query = query.gte('start_date', startDate);
      }
      if (endDate) {
        query = query.lte('start_date', endDate);
      }

      const { data: cases, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      const proBonoCases = cases || [];

      // Calcular estatísticas
      const totalCases = proBonoCases.length;
      const totalValue = proBonoCases.reduce((sum, case_) => sum + (case_.estimated_value || 0), 0);
      
      // Buscar métricas de impacto
      const { data: metrics, error: metricsError } = await supabase
        .from('probono_impact_metrics')
        .select('*');

      if (metricsError) {
        console.warn('Erro ao buscar métricas de impacto:', metricsError);
      }

      const totalHours = metrics?.filter(m => m.metric_type === 'hours_volunteered')
        .reduce((sum, m) => sum + (m.metric_value || 0), 0) || 0;
      
      const totalPeopleHelped = metrics?.filter(m => m.metric_type === 'people_helped')
        .reduce((sum, m) => sum + (m.metric_value || 0), 0) || 0;
      
      // Agrupar por categoria
      const categories: { [key: string]: { count: number; value: number; color: string } } = {};
      proBonoCases.forEach(case_ => {
        const category = case_.category_name || 'Outros';
        if (!categories[category]) {
          categories[category] = { count: 0, value: 0, color: case_.category_color || '#8B5CF6' };
        }
        categories[category].count++;
        categories[category].value += case_.estimated_value || 0;
      });

      // Agrupar por mês
      const monthlyData: { [key: string]: { cases: number; value: number; hours: number; people: number } } = {};
      proBonoCases.forEach(case_ => {
        const month = new Date(case_.start_date).toISOString().substring(0, 7); // YYYY-MM
        if (!monthlyData[month]) {
          monthlyData[month] = { cases: 0, value: 0, hours: 0, people: 0 };
        }
        monthlyData[month].cases++;
        monthlyData[month].value += case_.estimated_value || 0;
      });

      // Adicionar métricas mensais
      if (metrics) {
        metrics.forEach(metric => {
          const month = new Date(metric.recorded_date).toISOString().substring(0, 7);
          if (monthlyData[month]) {
            if (metric.metric_type === 'hours_volunteered') {
              monthlyData[month].hours += metric.metric_value || 0;
            } else if (metric.metric_type === 'people_helped') {
              monthlyData[month].people += metric.metric_value || 0;
            }
          }
        });
      }

      const monthlyDataArray = Object.entries(monthlyData)
        .map(([month, data]) => ({ month, ...data }))
        .sort((a, b) => a.month.localeCompare(b.month));

      return {
        totalCases,
        totalValue,
        totalHours,
        totalPeopleHelped,
        categories,
        monthlyData: monthlyDataArray,
        recentCases: proBonoCases.slice(0, 10) // Últimos 10 casos
      };
    } catch (error) {
      console.error('Erro ao gerar relatório Pro Bono:', error);
      throw error;
    }
  }

  /**
   * Adiciona um caso Pro Bono
   */
  static async addProBonoCase(caseData: {
    case_id?: string;
    administrative_process_id?: string;
    category_id: string;
    social_impact_description?: string;
    estimated_value: number;
    actual_value: number;
    start_date: string;
    end_date?: string;
    notes?: string;
  }): Promise<ProBonoCase> {
    try {
      const { data, error } = await supabase
        .from('probono_cases')
        .insert({
          ...caseData,
          status: 'active'
        })
        .select('*')
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao adicionar caso Pro Bono:', error);
      throw error;
    }
  }

  /**
   * Atualiza um caso Pro Bono
   */
  static async updateProBonoCase(caseId: string, updates: Partial<ProBonoCase>): Promise<ProBonoCase> {
    try {
      const { data, error } = await supabase
        .from('probono_cases')
        .update(updates)
        .eq('id', caseId)
        .select('*')
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao atualizar caso Pro Bono:', error);
      throw error;
    }
  }

  /**
   * Remove um caso Pro Bono
   */
  static async removeProBonoCase(caseId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('probono_cases')
        .delete()
        .eq('id', caseId);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao remover caso Pro Bono:', error);
      throw error;
    }
  }

  /**
   * Adiciona métrica de impacto
   */
  static async addImpactMetric(metricData: {
    probono_case_id: string;
    metric_type: string;
    metric_value: number;
    metric_unit: string;
    description?: string;
    recorded_date?: string;
  }): Promise<ProBonoImpactMetric> {
    try {
      const { data, error } = await supabase
        .from('probono_impact_metrics')
        .insert({
          ...metricData,
          recorded_date: metricData.recorded_date || new Date().toISOString().split('T')[0]
        })
        .select('*')
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao adicionar métrica de impacto:', error);
      throw error;
    }
  }

  /**
   * Exporta relatório Pro Bono para PDF
   */
  static async exportProBonoReport(startDate?: string, endDate?: string): Promise<Blob> {
    try {
      const reportData = await this.generateProBonoReport(startDate, endDate);
      
      // TODO: Implementar geração de PDF
      // Por enquanto, retorna um blob vazio
      return new Blob(['Relatório Pro Bono em desenvolvimento'], { type: 'text/plain' });
    } catch (error) {
      console.error('Erro ao exportar relatório Pro Bono:', error);
      throw error;
    }
  }
}
